import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import Icon1 from "@assets/icons/Icon";
import { useAtelierStore, JOB_STATUSES, getCachedWorkers, getJobStatusFromOrdre } from "@store/atelierStore";
import { WorkshopJob, WorkshopJobStatus, CarWindow } from "../../types/garage/atelier";
import { AvailabilityBlock, ZoneType } from "@store/disponibiliteStore";
import { formatTime, formatDateHour, positionTooltip } from "@utils/timeGrid";
import { showNotification } from "context/notification.service";
import {
  GridPreview,
  JobDragPreview,
  ChargeDragPreview,
  HOURS_START,
  HOURS_END,
  SNAP_HOUR,
  atelierMetrics,
  computeOverlapLayout,
} from "./gridTypes";
import { useDataStore } from "@store/user_org";
import { useFullClosures, isDateClosed } from "@utils/closedDates";
import { blockSegmentOnDate, firstOverlappingOffRegion } from "@utils/blockSegments";
import { ClosedDayOverlay } from "@components/ui/ClosedDayOverlay";
import OrdreModal from "@components/modals/OrdreModal";
import { findOrdreForPlate, buildOrdreFromAppointment, getOrdreById, useOrdreStore } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";
import OrdreInterventionsHint from "@components/ui/OrdreInterventionsHint";
import { PlanningContextMenu, ContextMenuState } from "@components/ui/PlanningContextMenu";

const CLICK_THRESHOLD = 10;

function getStatusConfig(status: WorkshopJobStatus) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

/** Get the display status config for a job, derived from its linked ordre */
function getJobDisplayConfig(job: WorkshopJob) {
  if (job.ordre_id) {
    const ordre = getOrdreById(job.ordre_id);
    if (ordre) return getStatusConfig(getJobStatusFromOrdre(ordre.status));
  }
  return getStatusConfig(job.status);
}

/** Light background colors for dispo zone overlays on the atelier grid */
const ZONE_BG: Record<ZoneType, string> = {
  mecanique:      "#EEF0FF",
  carrosserie:    "#E4E7FF",
  peinture:       "#FEE2E2",
  diagnostique:   "#D1FAE5",
  service_rapide: "#ECE9FF",
  campagnes:      "#FFEDD5",
  pause:          "#E6E6E6",
  conge:          "#E6E6E6",
};

/**
 * Return the zone type covering this hour for a worker, or null if none.
 * Honours multi-day/all-day spans. Congé wins over any work zone.
 * When `allowedMetiers` is non-empty, productive zones not in it are skipped.
 */
function getDispoZone(
  blocks: AvailabilityBlock[],
  workerId: string,
  date: string,
  hour: number,
  allowedMetiers?: string[],
): ZoneType | null {
  let fallback: ZoneType | null = null;
  let congeHit = false;
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const seg = blockSegmentOnDate(b, date);
    if (!seg) continue;
    if (hour >= seg.startHour && hour < seg.endHour) {
      const zone = (b.zoneType as ZoneType) || "mecanique";
      if (zone === "conge") { congeHit = true; continue; }
      if (
        allowedMetiers && allowedMetiers.length > 0 &&
        zone !== "pause" &&
        !allowedMetiers.includes(zone)
      ) {
        continue;
      }
      if (fallback === null) fallback = zone;
    }
  }
  return congeHit ? "conge" : fallback;
}

/** Subtract `holes` intervals from `base` intervals (both in hours). */
function subtractIntervals(base: [number, number][], holes: [number, number][]): [number, number][] {
  let segs = base;
  for (const [hs, he] of holes) {
    const next: [number, number][] = [];
    for (const [s, e] of segs) {
      if (he <= s || hs >= e) { next.push([s, e]); continue; }
      if (hs > s) next.push([s, hs]);
      if (he < e) next.push([he, e]);
    }
    segs = next;
  }
  return segs;
}

/** Merged intervals (in hours) where the worker actually works that day — congé subtracted. */
function workerWorkingIntervals(blocks: AvailabilityBlock[], workerId: string, date: string): [number, number][] {
  const work: [number, number][] = [];
  const conge: [number, number][] = [];
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const seg = blockSegmentOnDate(b, date);
    if (!seg) continue;
    if ((b.zoneType as ZoneType) === "conge") conge.push([seg.startHour, seg.endHour]);
    else work.push([seg.startHour, seg.endHour]);
  }
  work.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [s, e] of work) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return subtractIntervals(merged, conge);
}

/** Off (non-working) sub-segments within the hour cell [h, h+1] — complement of the working intervals. */
function offSegmentsInHour(h: number, working: [number, number][]): [number, number][] {
  const segs: [number, number][] = [];
  let cursor = h;
  for (const [s, e] of working) {
    if (e <= h || s >= h + 1) continue;
    const ws = Math.max(s, h);
    if (ws > cursor) segs.push([cursor, ws]);
    cursor = Math.max(cursor, Math.min(e, h + 1));
  }
  if (cursor < h + 1) segs.push([cursor, h + 1]);
  return segs.filter(([s, e]) => e - s > 1e-9);
}

export interface DebugLayer {
  workerId: string;
  startHour: number;
  durationHours: number;
  color: string;
  bgColor: string;
  label: string;
}

export function WorkerTimeGrid({
  gridScrollRef,
  gridPreview,
  setGridPreview,
  jobDragRef,
  blocks = [],
  debugLayers,
  allMode = false,
  selectedWorkerIds,
}: {
  gridScrollRef: React.RefObject<HTMLDivElement | null>;
  gridPreview: GridPreview | null;
  setGridPreview: (p: GridPreview | null) => void;
  jobDragRef: React.MutableRefObject<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>;
  blocks?: AvailabilityBlock[];
  debugLayers?: DebugLayer[];
  allMode?: boolean;
  selectedWorkerIds?: string[];
}) {
  const orgData = useDataStore((s) => s.orgData);

  // Subscribe to ordres so job colors update when ordre status changes
  const _ordres = useOrdreStore((s) => s.ordres);

  const selectedDate = useAtelierStore((s) => s.selectedDate);
  const compaction = useAtelierStore((s) => s.compaction);
  // Dynamic row-height metrics (shadow the module defaults) so the grid honours
  // the compact/normal/spread setting everywhere px math is used below.
  const { HOUR_BLOCK_HEIGHT, SNAP_PX } = atelierMetrics(compaction);
  // Height of one quarter-hour cell — MUST derive from HOUR_BLOCK_HEIGHT (4 per
  // hour) so the grid cells line up with jobs at any density. (Was hardcoded 32,
  // which only matched the 128px "normal" height and broke compact.)
  const QUARTER_H = HOUR_BLOCK_HEIGHT / 4;
  const closedDates = useFullClosures();
  const activeSpecialties = useAtelierStore((s) => s.activeSpecialties);
  const getFilteredJobs = useAtelierStore((s) => s.getFilteredJobs);
  const getVisibleWorkers = useAtelierStore((s) => s.getVisibleWorkers);
  const hoveredChargeId = useAtelierStore((s) => s.hoveredChargeId);
  const charges = useAtelierStore((s) => s.charges);
  const storeCarWindows = useAtelierStore((s) => s.carWindows);
  const confirmJob = useAtelierStore((s) => s.confirmJob);

  const jobs = getFilteredJobs();
  const allVisibleWorkers = getVisibleWorkers();
  const visibleWorkers = selectedWorkerIds && selectedWorkerIds.length > 0
    ? allVisibleWorkers.filter((w) => selectedWorkerIds.includes(w.id))
    : allVisibleWorkers;
  // Grey out the hours each shown worker doesn't work (from their dispo atelier
  // blocks): no block or congé. Computed per worker since each has its own
  // column; skipped only in the merged "Tous" column (allMode).
  const visibleWorkerIdsKey = visibleWorkers.map((w) => w.id).join(",");
  const offIntervalsByWorker = useMemo(() => {
    const map: Record<string, [number, number][]> = {};
    if (allMode) return map;
    for (const w of visibleWorkers) map[w.id] = workerWorkingIntervals(blocks, w.id, selectedDate);
    return map;
  }, [allMode, visibleWorkerIdsKey, blocks, selectedDate]);
  const [hoveredJobId, setHoveredJobId] = useState<string | null>(null);

  // Clear hovered job when date/jobs change (onMouseLeave won't fire if the element disappears)
  const jobIds = useMemo(() => jobs.map((j) => j.id).join(","), [jobs]);
  React.useEffect(() => {
    setHoveredJobId(null);
  }, [selectedDate, jobIds]);

  // Resolve carWindow: from hovered charge, hovered job, or the item being dragged
  const activeCarWindow = useMemo((): CarWindow | null => {
    // Priority 1: hovered charge in sidebar
    if (hoveredChargeId) {
      const charge = charges.find((c) => c.id === hoveredChargeId);
      if (charge?.carWindow) return charge.carWindow;
      if (charge?.ordre_id) return storeCarWindows[charge.ordre_id] || null;
      return null;
    }
    // Priority 2: hovered job on the grid (not dragging)
    if (hoveredJobId && !gridPreview) {
      const job = jobs.find((j) => j.id === hoveredJobId) ||
        useAtelierStore.getState().jobs.find((j) => j.id === hoveredJobId);
      if (job?.carWindow) return job.carWindow;
      if (job?.ordre_id) {
        return charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
          || storeCarWindows[job.ordre_id] || null;
      }
    }
    // Priority 3: job being dragged
    if (gridPreview?.type === "job") {
      const jobId = (gridPreview as JobDragPreview).jobId;
      const job = jobs.find((j) => j.id === jobId) ||
        useAtelierStore.getState().jobs.find((j) => j.id === jobId);
      if (job?.carWindow) return job.carWindow;
      if (job?.ordre_id) {
        return charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
          || storeCarWindows[job.ordre_id] || null;
      }
    }
    // Priority 3: charge being dragged onto grid
    if (gridPreview?.type === "charge") {
      const chargeId = (gridPreview as ChargeDragPreview).chargeId;
      const charge = charges.find((c) => c.id === chargeId);
      if (charge?.carWindow) return charge.carWindow;
      if (charge?.ordre_id) return storeCarWindows[charge.ordre_id] || null;
    }
    return null;
  }, [hoveredChargeId, hoveredJobId, charges, gridPreview, jobs, storeCarWindows]);

  // Compute car availability overlay for the day view
  const carOverlay = useMemo(() => {
    if (!activeCarWindow) return null;
    const cw = activeCarWindow;
    if (!cw.depositDate) return null;
    const restDate = cw.restitutionDate || cw.depositDate;
    if (selectedDate < cw.depositDate || selectedDate > restDate) return null;
    const startHour = selectedDate === cw.depositDate && cw.depositStartHour != null
      ? cw.depositStartHour : HOURS_START;
    const endHour = selectedDate === restDate && cw.restitutionStartHour != null
      ? cw.restitutionStartHour : HOURS_END;
    return { startHour, endHour };
  }, [activeCarWindow, selectedDate]);

  // Use persistent carWindows from store for conflict detection (survives charge deletion)
  const carWindowByOrdre = storeCarWindows;

  /** Check if a job is (partially) outside the car's presence window */
  const isJobOutsideCarWindow = useCallback((job: WorkshopJob): boolean => {
    if (!job.ordre_id) return false;
    const cw = carWindowByOrdre[job.ordre_id];
    if (!cw || !cw.depositDate) return false;
    const restDate = cw.restitutionDate || cw.depositDate;
    // Job is on a day entirely outside the car window
    if (job.date < cw.depositDate || job.date > restDate) return true;
    // Same-day checks
    const jobEnd = job.startHour + job.durationHours;
    if (job.date === cw.depositDate && cw.depositStartHour != null && job.startHour < cw.depositStartHour) return true;
    if (job.date === restDate && cw.restitutionStartHour != null && jobEnd > cw.restitutionStartHour) return true;
    return false;
  }, [carWindowByOrdre]);

  const highlightOrdreId = useAtelierStore((s) => s.highlightOrdreId);
  const highlightJobId = useAtelierStore((s) => s.highlightJobId);

  useEffect(() => {
    if (!highlightJobId) return;
    const job = jobs.find((j) => j.id === highlightJobId);
    if (!job) return;
    const scrollParent = gridScrollRef.current?.closest(".overflow-auto") as HTMLElement | null;
    if (scrollParent) {
      const targetPx = (job.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
      scrollParent.scrollTo({ top: targetPx - 80, behavior: "smooth" });
    }
  }, [highlightJobId, jobs]);

  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);
  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  const byWorker = useMemo(() => {
    const map: Record<string, WorkshopJob[]> = {};
    for (const w of getCachedWorkers()) map[w.id] = [];
    for (const j of jobs) {
      if (!map[j.workerId]) map[j.workerId] = [];
      map[j.workerId].push(j);
    }
    return map;
  }, [jobs]);

  // "Tous" mode: overlap layout for all jobs in a single merged column
  const allOverlapLayout = useMemo(() => {
    if (!allMode) return null;
    return computeOverlapLayout(jobs.map((j) => ({
      id: j.id,
      startHour: j.startHour,
      durationHours: j.durationHours,
    })));
  }, [allMode, jobs]);

  const workerNameById = useMemo(() => {
    if (!allMode) return {};
    const map: Record<string, string> = {};
    for (const w of getCachedWorkers()) map[w.id] = w.name;
    return map;
  }, [allMode]);

  const pendingDragRef = useRef<{
    job: WorkshopJob;
    mode: "move" | "resize";
    startX: number;
    startY: number;
    target: HTMLElement;
    pointerId: number;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, job: WorkshopJob, mode: "move" | "resize") => {
      if (e.button === 2) return;
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      clickStartRef.current = { x: e.clientX, y: e.clientY };
      pendingDragRef.current = {
        job,
        mode,
        startX: e.clientX,
        startY: e.clientY,
        target: e.target as HTMLElement,
        pointerId: e.pointerId,
      };
    },
    [],
  );

  const handlePointerMoveLocal = useCallback(
    (e: React.PointerEvent) => {
      const pending = pendingDragRef.current;
      if (!pending || jobDragRef.current) return;
      const dx = Math.abs(e.clientX - pending.startX);
      const dy = Math.abs(e.clientY - pending.startY);
      if (dx >= CLICK_THRESHOLD || dy >= CLICK_THRESHOLD) {
        // Release pointer capture so global handler takes over (survives re-renders)
        try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch (_) {}
        const { job, mode } = pending;

        // Teleport to green window if job is outside it
        if (mode === "move") {
          const cw = job.ordre_id ? carWindowByOrdre[job.ordre_id] : null;
          if (!cw || !cw.depositDate) {
            const reason = !job.ordre_id
              ? "Aucun ordre associé à ce travail"
              : "Aucune fenêtre véhicule définie (pas de date de réception)";
            showNotification({ type: "default", message: `Pas de fenêtre véhicule : ${reason}` });
          } else if (isJobOutsideCarWindow(job)) {
            const { selectedDate, setSelectedDate } = useAtelierStore.getState();
            if (selectedDate !== cw.depositDate) {
              setSelectedDate(cw.depositDate);
            }
          }
        }

        jobDragRef.current = {
          jobId: job.id,
          mode,
          startY: pending.startY,
          origStartHour: job.startHour,
          origDuration: job.durationHours,
          origWorkerId: job.workerId,
        };
        setGridPreview({
          type: "job",
          jobId: job.id,
          startHour: job.startHour,
          durationHours: job.durationHours,
          workerId: job.workerId,
        });
        pendingDragRef.current = null;
      }
    },
    [setGridPreview, jobDragRef, carWindowByOrdre, isJobOutsideCarWindow],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent, job: WorkshopJob) => {
      pendingDragRef.current = null;
      const start = clickStartRef.current;
      if (
        start &&
        Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
        Math.abs(e.clientY - start.y) < CLICK_THRESHOLD
      ) {
        if (job.autoGenerated) {
          confirmJob(job.id);
          clickStartRef.current = null;
          return;
        }
        if (job.ordre_id) {
          ordresApi.get(job.ordre_id).then((ordre) => setSelectedOrdre(ordre)).catch(() => {
            setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
          });
        } else {
          setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
        }
      }
      clickStartRef.current = null;
    },
    [confirmJob],
  );

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Column headers */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {allMode ? (
          <div
            className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
            style={{
              borderRight: "1px solid #D9D9D9",
              borderBottom: "1px solid #D9D9D9",
              background: "#FFF",
              color: "#525252",
              fontFamily: "Poppins",
            }}
          >
            Tous
          </div>
        ) : visibleWorkers.map((w) => (
          <div
            key={w.id}
            className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
            style={{
              borderRight: "1px solid #D9D9D9",
              borderBottom: "1px solid #D9D9D9",
              background: "#FFF",
              color: "#525252",
              fontFamily: "Poppins",
            }}
          >
            {w.name}
          </div>
        ))}
      </div>

      <div ref={gridScrollRef} className="relative">
        {/* Org closure day indicator */}
        {isDateClosed(selectedDate, closedDates) && <ClosedDayOverlay />}
        {/* Car availability overlay when hovering a charge */}
        {carOverlay && (() => {
          const totalHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const topPx = (carOverlay.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const bottomPx = (carOverlay.endHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const hourLabelW = 32 + 24 + 1; // hourLabel + minuteCol + border
          return (
            <>
              {/* Dim area before car arrives */}
              {topPx > 0 && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: 0,
                    left: hourLabelW,
                    right: 0,
                    height: topPx,
                    background: "rgba(0,0,0,0.06)",
                    zIndex: 2,
                  }}
                />
              )}
              {/* Green zone where car is present */}
              <div
                className="absolute pointer-events-none"
                style={{
                  top: topPx,
                  left: hourLabelW,
                  right: 0,
                  height: Math.max(bottomPx - topPx, 0),
                  background: "rgba(45, 212, 191, 0.08)",
                  borderTop: "2px solid rgba(45, 212, 191, 0.4)",
                  borderBottom: "2px solid rgba(45, 212, 191, 0.4)",
                  zIndex: 2,
                }}
              />
              {/* Dim area after car leaves */}
              {bottomPx < totalHeight && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: bottomPx,
                    left: hourLabelW,
                    right: 0,
                    height: totalHeight - bottomPx,
                    background: "rgba(0,0,0,0.06)",
                    zIndex: 2,
                  }}
                />
              )}
            </>
          );
        })()}
        <div className="flex flex-col">
          {hours.map((h) => {
            return (
              <div
                key={h}
                className="flex flex-row"
                style={{
                  height: HOUR_BLOCK_HEIGHT,
                  borderBottom: "2px solid #BDBDBD",
                }}
              >
                <div
                  className="flex items-start text-h4 font-medium"
                  style={{
                    width: hourLabelWidth,
                    paddingLeft: 7,
                    paddingTop: 2,
                    fontFamily: "Poppins",
                    color: "#A1A1A1",
                    flexShrink: 0,
                  }}
                >
                  {h}h
                </div>
                <div
                  className="flex flex-col"
                  style={{
                    width: minuteColWidth,
                    borderLeft: "1px solid #D9D9D9",
                    borderRight: "1px solid #D9D9D9",
                    flexShrink: 0,
                  }}
                >
                  {["00", "15", "30", "45"].map((m) => (
                    <div
                      key={m}
                      className="flex items-center justify-center text-h4 font-medium"
                      style={{
                        height: QUARTER_H,
                        borderBottom: "1px solid #D9D9D9",
                        fontFamily: "Poppins",
                        color: "#A1A1A1",
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>

                {(allMode ? [{ id: "tous" as string, name: "Tous" }] : visibleWorkers).map((worker) => {
                  const colJobs = allMode
                    ? jobs.filter((j) => j.startHour >= h && j.startHour < h + 1)
                    : (byWorker[worker.id] || []).filter((j) => j.startHour >= h && j.startHour < h + 1);
                  const dispoZone = allMode ? null : (worker as any).blockedHours?.includes(h) ? "pause" as ZoneType : getDispoZone(blocks, worker.id, selectedDate, h, activeSpecialties);
                  const cellBg = dispoZone ? ZONE_BG[dispoZone] : undefined;
                  const ghost =
                    gridPreview &&
                    (allMode || gridPreview.workerId === worker.id) &&
                    gridPreview.startHour >= h &&
                    gridPreview.startHour < h + 1
                      ? gridPreview
                      : null;

                  return (
                    <div
                      key={worker.id}
                      data-worker-col={worker.id}
                      className="flex-1 relative"
                      style={{ borderRight: "1px solid #D9D9D9" }}
                    >
                      {/* One cell per quarter-hour */}
                      {[0, 1, 2, 3].map((q) => (
                        <div
                          key={q}
                          style={{
                            height: QUARTER_H,
                            borderBottom: "1px solid #D9D9D9",
                            background: cellBg ?? undefined,
                          }}
                        />
                      ))}

                      {/* Non-working hours (no dispo block / congé) — greyed out per worker column */}
                      {!allMode &&
                        offSegmentsInHour(h, offIntervalsByWorker[worker.id] || []).map(([os, oe], idx) => (
                          <div
                            key={`off-${idx}`}
                            className="absolute left-0 right-0 pointer-events-none"
                            style={{
                              top: ((os - h) / SNAP_HOUR) * SNAP_PX,
                              height: ((oe - os) / SNAP_HOUR) * SNAP_PX,
                              background:
                                "repeating-linear-gradient(45deg, rgba(120,120,120,0.13), rgba(120,120,120,0.13) 6px, rgba(120,120,120,0.20) 6px, rgba(120,120,120,0.20) 12px)",
                              zIndex: 3,
                            }}
                          />
                        ))}

                      {/* Grid ghost preview (hidden when merge — target job itself highlights) */}
                      {ghost && !(ghost.type === "job" && (ghost as JobDragPreview).mergeTarget) &&
                        (() => {
                          const topPx =
                            ((ghost.startHour - h) / SNAP_HOUR) * SNAP_PX;
                          const heightPx =
                            (ghost.durationHours / SNAP_HOUR) * SNAP_PX;
                          const isCharge = ghost.type === "charge";
                          const isMerge = !isCharge && !!(ghost as JobDragPreview).mergeTarget;

                          // Check if ghost is outside car window or overlaps another job → red
                          let ghostOutside = !!ghost.overlap;
                          // Also red when dropping on a break / non-working area
                          // (lunch gap, pause, congé, or a slot with no work zone).
                          if (
                            !allMode &&
                            firstOverlappingOffRegion(
                              blocks,
                              worker.id,
                              selectedDate,
                              ghost.startHour,
                              ghost.startHour + ghost.durationHours,
                            )
                          ) {
                            ghostOutside = true;
                          }
                          const cw = activeCarWindow;
                          if (cw?.depositDate) {
                            const depDate = cw.depositDate;
                            const restDate = cw.restitutionDate || depDate;
                            const ghostEnd = ghost.startHour + ghost.durationHours;
                            if (selectedDate < depDate || selectedDate > restDate) {
                              ghostOutside = true;
                            } else if (selectedDate === depDate && cw.depositStartHour != null && ghost.startHour < cw.depositStartHour) {
                              ghostOutside = true;
                            } else if (selectedDate === restDate && cw.restitutionStartHour != null && ghostEnd > cw.restitutionStartHour) {
                              ghostOutside = true;
                            }
                          }

                          const color = ghostOutside
                            ? "#DC2626"
                            : isMerge
                              ? "#6366F1"
                              : isCharge
                                ? "#6366F1"
                                : (() => {
                                    const j = jobs.find(
                                      (jj) =>
                                        jj.id === (ghost as JobDragPreview).jobId,
                                    );
                                    return j
                                      ? getJobDisplayConfig(j).color
                                      : "#6366F1";
                                  })();
                          const bg = ghostOutside
                            ? "#FEE2E2"
                            : isMerge
                              ? "#E0E7FF"
                              : isCharge
                                ? "#E0E6FF"
                                : (() => {
                                    const j = jobs.find(
                                      (jj) =>
                                        jj.id === (ghost as JobDragPreview).jobId,
                                    );
                                    return j
                                      ? getJobDisplayConfig(j).bgColor
                                      : "#E0E6FF";
                                  })();
                          return (
                            <div
                              className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                              style={{
                                top: topPx,
                                height: Math.max(heightPx, 20),
                                background: bg,
                                border: `2px ${isMerge ? "solid" : "dashed"} ${color}`,
                                borderRadius: 5,
                                margin: "0 2px",
                                zIndex: 40,
                                opacity: isMerge ? 0.85 : 0.7,
                              }}
                            >
                              <span
                                className="text-h4 font-medium"
                                style={{ color, lineHeight: "100%" }}
                              >
                                {ghostOutside && "⚠ "}
                                {isMerge ? "Fusionner" : `${formatTime(ghost.startHour)} – ${formatTime(ghost.startHour + ghost.durationHours)}`}
                              </span>
                            </div>
                          );
                        })()}

                      {/* Debug layers — clip each layer to this hour cell */}
                      {debugLayers?.filter(
                        (l) => l.workerId === worker.id &&
                          l.startHour < h + 1 &&
                          l.startHour + l.durationHours > h,
                      ).map((layer, i) => {
                        const clippedStart = Math.max(layer.startHour, h);
                        const clippedEnd = Math.min(layer.startHour + layer.durationHours, h + 1);
                        const topPx = ((clippedStart - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = ((clippedEnd - clippedStart) / SNAP_HOUR) * SNAP_PX;
                        const isFirstCell = layer.startHour >= h;
                        return (
                          <div
                            key={`layer-${i}`}
                            className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 4),
                              background: layer.bgColor,
                              borderLeft: `3px solid ${layer.color}`,
                              margin: "0 2px",
                              zIndex: 35,
                              opacity: 0.6,
                            }}
                          >
                            {isFirstCell && heightPx >= 20 && (
                              <span style={{ fontFamily: "Poppins", fontSize: 9, fontWeight: 600, color: layer.color, lineHeight: "100%" }}>
                                {layer.label}
                              </span>
                            )}
                          </div>
                        );
                      })}

                      {/* Jobs */}
                      {colJobs.map((job) => {
                        const config = getJobDisplayConfig(job);
                        const topPx =
                          ((job.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx =
                          (job.durationHours / SNAP_HOUR) * SNAP_PX;
                        const isDragSource =
                          gridPreview?.type === "job" &&
                          (gridPreview as JobDragPreview).jobId === job.id;
                        const isMergeTarget =
                          gridPreview?.type === "job" &&
                          (gridPreview as JobDragPreview).mergeTargetJobId === job.id;
                        const isHovered = hoveredJobId === job.id;
                        const outsideWindow = isJobOutsideCarWindow(job);
                        const isAuto = job.autoGenerated;
                        const isHighlighted = (highlightJobId != null && job.id === highlightJobId) || (highlightOrdreId != null && job.ordre_id === highlightOrdreId);
                        const overlap = allMode && allOverlapLayout ? allOverlapLayout.get(job.id) : null;
                        const olLeft = overlap ? `calc(${(overlap.column / overlap.totalColumns) * 100}% + 2px)` : undefined;
                        const olWidth = overlap ? `calc(${(1 / overlap.totalColumns) * 100}% - 4px)` : undefined;
                        return (
                          <div
                            key={job.id}
                            className={`absolute ${overlap ? "" : "left-0 right-0 "}flex flex-col${isAuto ? " auto-placed-pulse" : ""}${isHighlighted ? " highlight-pulse" : ""}`}
                            style={{
                              ...(overlap ? { left: olLeft, width: olWidth } : {}),
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: isMergeTarget ? "#E0E7FF" : outsideWindow ? "#FEE2E2" : config.bgColor,
                              border: isMergeTarget
                                ? "2px solid #6366F1"
                                : isHighlighted
                                ? "2px solid #6366F1"
                                : isAuto
                                ? `2px dashed ${outsideWindow ? "#FCA5A5" : config.borderColor}`
                                : `1px solid ${outsideWindow ? "#FCA5A5" : config.borderColor}`,
                              boxShadow: isMergeTarget
                                ? "0px 0px 16px rgba(99, 102, 241, 0.5)"
                                : isHighlighted
                                ? "0px 0px 12px rgba(99, 102, 241, 0.5)"
                                : isHovered
                                ? "0px 4px 12px rgba(0,0,0,0.25)"
                                : undefined,
                              borderRadius: 5,
                              padding: "3px 4px",
                              overflow: isMergeTarget ? "visible" : "hidden",
                              zIndex: isDragSource ? 5 : isMergeTarget ? 20 : isHovered ? 15 : 10,
                              margin: "0 2px",
                              cursor: "grab",
                              userSelect: "none",
                              opacity: isDragSource ? 0.35 : (hoveredJobId && !isHovered && !isMergeTarget) ? 0.3 : isAuto ? 0.85 : 1,
                              transition: "opacity 0.15s, box-shadow 0.15s, background 0.15s, border 0.15s",
                            }}
                            onPointerDown={(e) =>
                              handlePointerDown(e, job, "move")
                            }
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!job.ordre_id) return;
                              const ordre = getOrdreById(job.ordre_id);
                              if (!ordre) return;
                              setContextMenu({ x: e.clientX, y: e.clientY, ordreId: ordre.id, currentStatus: ordre.status });
                            }}
                            onPointerMove={handlePointerMoveLocal}
                            onPointerUp={(e) => handlePointerUp(e, job)}
                            onMouseEnter={() => setHoveredJobId(job.id)}
                            onMouseLeave={() => setHoveredJobId(null)}
                          >
                            {/* Hover tooltip */}
                            {isHovered && !gridPreview && (
                              <div
                                className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                                style={{ maxWidth: 320, minWidth: 140 }}
                                ref={(el) => { if (el) positionTooltip(el); }}
                              >
                                <div className="text-h4 font-medium">{job.clientName}</div>
                                {job.vehicleName && <div className="text-h5 text-neutral-200">{job.vehicleName}</div>}
                                {job.plate && <div className="text-h5 text-neutral-300">{job.plate}</div>}
                                <div className="text-h5 text-neutral-300">
                                  {formatTime(job.startHour)} — {formatTime(job.startHour + job.durationHours)}
                                </div>
                                {job.carWindow && (
                                  <div className="text-h5 text-neutral-400 mt-1">
                                    Réception : {formatDateHour(job.carWindow.depositDate, job.carWindow.depositStartHour)}
                                    {" — "}
                                    Restitution : {formatDateHour(job.carWindow.restitutionDate, job.carWindow.restitutionStartHour)}
                                  </div>
                                )}
                                {/* Interventions rendered like the planning reception
                                    tooltip: vertical "Interventions (N)" list with hours,
                                    fetched from the ordre (fallback: the job's labels). */}
                                <OrdreInterventionsHint ordreId={job.ordre_id} fallbackLabels={job.interventions} />
                                {outsideWindow && (
                                  <div className="text-h5 text-red-400 mt-1 font-medium">Véhicule absent à cet horaire</div>
                                )}
                                {isAuto
                                  ? <div className="text-h5 text-indigo-300 mt-1">Placement automatique - cliquer pour confirmer</div>
                                  : <div className="text-h5 text-neutral-400 mt-1">Cliquer pour modifier</div>
                                }
                              </div>
                            )}
                            <div
                              className="flex flex-col"
                              style={{
                                gap: 0,
                                minWidth: 0,
                                overflow: "hidden",
                                flex: 1,
                              }}
                            >
                              <div className="flex flex-row items-center" style={{ gap: 4, minWidth: 0 }}>
                                {outsideWindow && (
                                  <div className="flex items-start" style={{ flexShrink: 0 }} title="Véhicule absent à cet horaire">
                                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                                      <path d="M12 2L1 21h22L12 2z" fill="#EF4444" />
                                      <text x="12" y="18" textAnchor="middle" fill="#FFF" fontSize="14" fontWeight="bold">!</text>
                                    </svg>
                                  </div>
                                )}
                                {isAuto && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 600,
                                      color: "#FFF",
                                      background: "#6366F1",
                                      borderRadius: 3,
                                      padding: "0 3px",
                                      lineHeight: "14px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    Auto
                                  </span>
                                )}
                                {allMode && workerNameById[job.workerId] && (
                                  <span
                                    style={{
                                      fontSize: 8,
                                      fontWeight: 600,
                                      color: "#6B7280",
                                      background: "#F3F4F6",
                                      borderRadius: 3,
                                      padding: "0 3px",
                                      lineHeight: "13px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {workerNameById[job.workerId]}
                                  </span>
                                )}
                                <span
                                  className="text-h4 font-medium"
                                  style={{
                                    lineHeight: "140%",
                                    color: outsideWindow ? "#DC2626" : config.color,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                  }}
                                >
                                  {job.clientName}
                                </span>
                                {job.hasCarIcon && (
                                  <div className="flex items-start" style={{ flexShrink: 0 }}>
                                    <Icon1 name="car-reception" size={14} color={outsideWindow ? "#DC2626" : config.color} />
                                  </div>
                                )}
                              </div>
                              {heightPx > 30 && job.vehicleName && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {job.vehicleName}
                                </span>
                              )}
                              {heightPx > 30 && job.plate && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {job.plate}
                                </span>
                              )}
                              {heightPx > 60 && job.carWindow && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {formatDateHour(job.carWindow.depositDate, job.carWindow.depositStartHour)} — {formatDateHour(job.carWindow.restitutionDate, job.carWindow.restitutionStartHour)}
                                </span>
                              )}
                              {heightPx > 80 && job.interventions && job.interventions.length > 0 && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {job.interventions.slice(0, 3).join(", ")}
                                  {job.interventions.length > 3 && ` +${job.interventions.length - 3}`}
                                </span>
                              )}
                            </div>
                            {heightPx >= 24 && (
                              <div
                                className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                                style={{ height: 8, cursor: "ns-resize" }}
                                onPointerDown={(e) => {
                                  e.stopPropagation();
                                  handlePointerDown(e, job, "resize");
                                }}
                                onPointerMove={handlePointerMoveLocal}
                              >
                                <div
                                  style={{
                                    width: 20,
                                    height: 2,
                                    borderRadius: 1,
                                    background: isMergeTarget ? "#6366F1" : config.color,
                                    opacity: 0.4,
                                  }}
                                />
                              </div>
                            )}
                            {/* Merge preview: dashed extension showing added duration */}
                            {isMergeTarget && gridPreview && (() => {
                              const draggedDuration = (gridPreview as JobDragPreview).durationHours;
                              const mergedTotal = job.durationHours + draggedDuration;
                              const extensionPx = (draggedDuration / SNAP_HOUR) * SNAP_PX;
                              return (
                                <div
                                  className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                                  style={{
                                    top: heightPx,
                                    height: extensionPx,
                                    background: "rgba(99, 102, 241, 0.08)",
                                    border: "2px dashed #6366F1",
                                    borderTop: "none",
                                    borderRadius: "0 0 5px 5px",
                                    margin: "0 2px",
                                    zIndex: 20,
                                  }}
                                >
                                  <span className="text-h4 font-medium" style={{ color: "#6366F1", lineHeight: "100%" }}>
                                    {formatTime(job.startHour)} - {formatTime(job.startHour + mergedTotal)} ({mergedTotal}h)
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
          onDelete={() => setSelectedOrdre(null)}
        />
      )}
      {contextMenu && (
        <PlanningContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />
      )}
    </div>
  );
}
